/**
 * What a tooltip says about an item.
 *
 * Apart from the component that draws it, because it is a function of the item
 * rather than a piece of interface, and because a file that exports both is a
 * file the dev server cannot refresh cleanly.
 */

import type { ItemDefinitionWire } from '@inverse/protocol';
import type { TooltipContent } from './Tooltip';

/** The names of the fourteen bonuses, in the order they travel. */
const BONUS_LABELS = [
  'Stab',
  'Slash',
  'Bash',
  'Ranged',
  'Magic',
  'Stab defence',
  'Slash defence',
  'Bash defence',
  'Ranged defence',
  'Magic defence',
  'Strength',
  'Ranged strength',
  'Magic damage',
  'Faith',
] as const;

/**
 * What to say about an item.
 *
 * A plain thing gets its name and nothing else, because a tooltip that says
 * "Ashen log: it is a log" is noise. Anything with numbers on it gets the
 * numbers, since that is the whole reason somebody is hovering.
 */
export function itemTooltip(
  item: ItemDefinitionWire | undefined,
  fallback: string,
  extra?: string,
  /**
   * What is in that slot now, for anything that would be worn.
   *
   * The numbers on a sword mean nothing on their own: what somebody deciding
   * whether to put it on wants is the difference against the one already in
   * their hand, which is the question they are actually asking.
   */
  against?: ItemDefinitionWire,
): TooltipContent {
  if (!item) return { title: fallback };

  const lines: string[] = [];
  const stats: { label: string; value: string }[] = [];

  if (item.heals > 0) {
    lines.push(`Puts back ${item.heals} health when eaten.`);
  }

  if (item.equipSlot >= 0) {
    if (item.speed > 0) {
      // Ticks are the game's own clock; seconds are what a person counts in.
      lines.push(`Swings every ${item.speed} ticks, about ${(item.speed * 0.6).toFixed(1)}s.`);
    }

    // Against whatever is in that slot now, when there is something to compare
    // with and it is not the same thing. A bonus that does not change on either
    // side is left out: what matters is what would be different.
    const comparing = against !== undefined && against.id !== item.id;

    for (const [at, label] of BONUS_LABELS.entries()) {
      const held = item.bonuses[at] ?? 0;
      const worn = comparing ? (against.bonuses[at] ?? 0) : 0;
      if (held === 0 && worn === 0) continue;

      const said = held > 0 ? `+${held}` : String(held);
      const change = held - worn;

      stats.push({
        label,
        value: comparing && change !== 0 ? `${said} (${change > 0 ? '+' : ''}${change})` : said,
      });
    }

    if (comparing) {
      lines.push(`Against your ${against.name.toLowerCase()}.`);

      if (item.speed > 0 && against.speed > 0 && item.speed !== against.speed) {
        lines.push(item.speed < against.speed ? 'Faster than it.' : 'Slower than it.');
      }
    }

    if (item.needs) lines.push(`Needs ${item.needs}.`);
  }

  if (item.value > 0) lines.push(`Worth ${item.value}.`);

  return {
    title: item.name,
    // What a plain click does, which for anything in the bag is the first thing
    // its own menu offers, and Use for anything with no menu of its own. Said
    // here as well as on the menu, because a tooltip that lists what a sword is
    // worth and not what pressing it does has answered the wrong question.
    action: item.actions[0] ?? 'Use',
    lines,
    stats,
    note: extra ?? item.examine,
  };
}
